import { ComponentPropsWithoutRef, FC } from "react";
import { twMerge } from "tailwind-merge";
import { useLocale, useMessages } from "../context/i18n";
import { pricingCards as enPricingCards } from "../dictionaries/en/pricing-cards";
import { formatUsdIn } from "../utils/format-number";
import {
  calculateMaxYearlySavings,
  usePricingExperiment,
} from "../hooks/usePricingExperiment";
import OptionsTabs, { OptionTab } from "./OptionsTabs";
import Typography from "./Typography";

type PricingTabsProps = {
  onSelect: (value: "monthly" | "yearly") => void;
  selected: "monthly" | "yearly";
} & ComponentPropsWithoutRef<"div">;

const PricingTabs: FC<PricingTabsProps> = ({ onSelect, selected, ...rest }) => {
  const pricingConfig = usePricingExperiment();
  const lang = useLocale();
  const m = useMessages("pricingCards", enPricingCards);

  // Calculate the appropriate label based on experiment variant
  const getYearlyLabel = () => {
    if (pricingConfig.showDiscountPercentage) {
      return m.savePercent.replace(
        "{percent}",
        String(Math.round(pricingConfig.discountPercentage * 100)),
      );
    }

    if (pricingConfig.showSavingsAmount) {
      // Monthly prices for max savings calculation
      const monthlyPrices = [29, 79, 189]; // Basic, Pro, Pro+ monthly prices
      const maxSavings = calculateMaxYearlySavings(
        monthlyPrices,
        pricingConfig.discountPercentage,
      );
      return m.saveAmount.replace("{amount}", formatUsdIn(lang, maxSavings));
    }

    // Default for control and line-through variants
    return m.saveAmount.replace("{amount}", formatUsdIn(lang, 190));
  };

  const options: OptionTab<"monthly" | "yearly">[] = [
    { label: m.monthly, value: "monthly" },
    {
      label: m.yearly,
      value: "yearly",
      trailing: (
        <Typography
          variant="button1"
          className="text-blue-500 whitespace-nowrap"
        >
          {getYearlyLabel()}
        </Typography>
      ),
    },
  ];

  return (
    <OptionsTabs<"monthly" | "yearly">
      {...rest}
      value={selected}
      onOptionChange={onSelect}
      size="l"
      options={options}
      className={twMerge("inline-flex mx-auto", rest.className)}
    />
  );
};

export default PricingTabs;
